import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Loader2, Plus, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { formatEurCompact } from "@/lib/formatters";
import { updateProject } from "@/api/projects";
import { useProjectStore } from "@/stores/project.store";
import { usePermissions } from "@/hooks/usePermissions";
import { useDepartments, useNafSections } from "@/hooks/useReferences";
import type {
  Department,
  NafSection,
  Project,
  SearchCriteria,
} from "@/api/types";

const LEGAL_FORMS = ["SARL", "SAS", "SA", "EURL", "SASU", "SCI", "SNC", "SCOP"];

const REVENUE_MAX = 100_000_000;
const REVENUE_STEP = 100_000;
const NET_RESULT_MAX = 5_000_000;
const NET_RESULT_STEP = 10_000;
const EQUITY_MAX = 5_000_000;
const EQUITY_STEP = 10_000;

interface SearchCriteriaSectionProps {
  project: Project;
}

export function SearchCriteriaSection({ project }: SearchCriteriaSectionProps) {
  const canEdit = usePermissions().canEditScoring;
  const setCurrentProject = useProjectStore((s) => s.setCurrentProject);
  const setProjects = useProjectStore((s) => s.setProjects);
  const projects = useProjectStore((s) => s.projects);

  // Read as-is. There used to be a `normalize()` here rebuilding the
  // object key by key, which was the ONLY contract this data had — and it
  // was a whitelist of thirteen, so `entreprise_active` (stored since the
  // legacy import, read by the Pappers job) was silently dropped on every
  // read. The server now owns the shape: `SearchCriteria` is
  // `extra="forbid"`, bounded, and range-ordered, so re-deriving it here
  // would be a second, weaker copy of a contract that already exists.
  const initial = useMemo<SearchCriteria>(
    () => (project.config?.search_criteria as SearchCriteria | undefined) ?? {},
    [project.config],
  );

  const [draft, setDraft] = useState<SearchCriteria>(initial);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setDraft(initial);
  }, [initial]);

  const isDirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(initial),
    [draft, initial],
  );

  function update(patch: Partial<SearchCriteria>) {
    setDraft((d) => ({ ...d, ...patch }));
  }

  /** Click cycle on a legal-form chip:
   *   ignore → include → exclude → ignore. */
  function toggleLegalForm(form: string) {
    const inc = new Set(draft.forme_juridique_include ?? []);
    const exc = new Set(draft.forme_juridique_exclude ?? []);
    if (inc.has(form)) {
      inc.delete(form);
      exc.add(form);
    } else if (exc.has(form)) {
      exc.delete(form);
    } else {
      inc.add(form);
    }
    update({
      forme_juridique_include: Array.from(inc),
      forme_juridique_exclude: Array.from(exc),
    });
  }

  async function save() {
    setSubmitting(true);
    try {
      // The typed field, not `config: { search_criteria }`. The config
      // patch is free-form and shallow-merged: it skipped validation
      // entirely (a typo was stored, never rejected) and kept unsent keys
      // alive, which is exactly how `entreprise_active` survived a UI that
      // could not see it. This path validates and replaces wholesale —
      // which is safe only because every one of the fourteen keys now has
      // an editor below.
      const updated = await updateProject(project.slug, {
        search_criteria: draft,
      });
      setCurrentProject(updated);
      setProjects(
        projects.map((p) => (p.slug === updated.slug ? updated : p)),
      );
      toast.success("Search criteria saved.");
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  function reset() {
    setDraft(initial);
  }

  return (
    <section className="space-y-5">
      <header>
        <h2 className="text-lg font-medium tracking-tight text-slate-900">
          Search criteria
        </h2>
        <p className="text-sm text-slate-500">
          Constraints for the Pappers sourcing pass.
        </p>
      </header>

      {/* The job exists now — engine/m_and_a/pappers_search.build_search_params
        *  reads these keys — so the old "nothing reads this" banner was
        *  false. What is still true is that it has no API key and is
        *  switched off, which is one word away from the text below when it
        *  is turned on. */}
      <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        <p className="text-sm text-amber-900">
          <span className="font-medium">
            These are the criteria the Pappers sourcing will send on every
            run.
          </span>{" "}
          The job is built and deployed but not yet activated, so nothing is
          being sourced today.
        </p>
      </div>

      {/* Which criterion costs what. Pappers bills 0.1 credit per row
        *  returned by /recherche, so the only criteria that save money are
        *  the ones that stop a row from being returned at all. */}
      <div className="space-y-2 rounded-xl border border-slate-900/[0.06] bg-slate-50/60 px-4 py-3 text-xs text-slate-600">
        <div className="flex items-start gap-2">
          <EnforcementTag kind="pappers" />
          <p>
            Sent as a query parameter. Rows you exclude are never returned
            and never billed — <strong>this is where tightening saves
            credits</strong>.
          </p>
        </div>
        <div className="flex items-start gap-2">
          <EnforcementTag kind="unapplied" />
          <p>
            Pappers has no parameter for it, and nothing filters it after
            the fact either, so every row is still returned and billed 0.1
            credit. Tightening these changes neither the results nor the
            bill today.
          </p>
        </div>
      </div>

      <fieldset
        disabled={!canEdit}
        className="space-y-5 disabled:opacity-60"
      >
        <Card title="Financials">
          <DualRangeRow
            label="Revenue range"
            enforcement="pappers"
            min={0}
            max={REVENUE_MAX}
            step={REVENUE_STEP}
            value={[
              draft.ca_min ?? 0,
              draft.ca_max ?? REVENUE_MAX,
            ]}
            displayLeft={formatEurCompact(draft.ca_min ?? 0)}
            displayRight={formatEurCompact(draft.ca_max ?? REVENUE_MAX)}
            onChange={([min, max]) =>
              update({ ca_min: min, ca_max: max })
            }
          />
          <SliderRow
            label="Net result min"
            enforcement="pappers"
            min={0}
            max={NET_RESULT_MAX}
            step={NET_RESULT_STEP}
            value={draft.resultat_net_min ?? 0}
            display={formatEurCompact(draft.resultat_net_min ?? 0)}
            onChange={(n) => update({ resultat_net_min: n })}
          />
          <SliderRow
            label="Equity min"
            enforcement="unapplied"
            min={0}
            max={EQUITY_MAX}
            step={EQUITY_STEP}
            value={draft.fonds_propres_min ?? 0}
            display={formatEurCompact(draft.fonds_propres_min ?? 0)}
            onChange={(n) => update({ fonds_propres_min: n })}
          />
        </Card>

        <Card title="Director" enforcement="pappers">
          <DualRangeRow
            label="Director age"
            min={20}
            max={100}
            step={1}
            value={[draft.age_min ?? 20, draft.age_max ?? 100]}
            displayLeft={String(draft.age_min ?? 20)}
            displayRight={String(draft.age_max ?? 100)}
            onChange={([min, max]) =>
              update({ age_min: min, age_max: max })
            }
          />
        </Card>

        {/* The one card whose two halves are billed differently, and it
          *  is a single control: `forme_juridique_include` becomes the
          *  `forme_juridique` parameter, `forme_juridique_exclude` has no
          *  negative form and reaches nothing. */}
        <Card title="Legal forms">
          <p className="-mt-2 mb-1 text-xs text-slate-500">
            Click to cycle:{" "}
            <span className="text-emerald-700">include</span> →{" "}
            <span className="text-rose-700">exclude</span> → ignore.
          </p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1.5">
              <EnforcementTag kind="pappers" />
              <span className="text-emerald-700">include</span>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <EnforcementTag kind="unapplied" />
              <span className="text-rose-700">exclude</span>
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {LEGAL_FORMS.map((form) => {
              const included =
                draft.forme_juridique_include?.includes(form) ?? false;
              const excluded =
                draft.forme_juridique_exclude?.includes(form) ?? false;
              return (
                <button
                  key={form}
                  type="button"
                  onClick={() => toggleLegalForm(form)}
                  className={cn(
                    "rounded-full border px-3 py-1.5 text-sm transition-all",
                    included &&
                      "border-emerald-200 bg-emerald-50 text-emerald-700",
                    excluded &&
                      "border-rose-200 bg-rose-50 text-rose-700 line-through",
                    !included &&
                      !excluded &&
                      "border-slate-200 bg-slate-50 text-slate-500",
                  )}
                >
                  {included && "✓ "}
                  {excluded && "✗ "}
                  {form}
                </button>
              );
            })}
          </div>
        </Card>

        <Card title="Excluded departments" enforcement="pappers">
          <DepartmentsPicker
            value={draft.departements_exclus ?? []}
            onChange={(next) => update({ departements_exclus: next })}
          />
        </Card>

        <Card title="Excluded NAF sectors" enforcement="pappers">
          <NafSectionsPicker
            value={draft.secteurs_naf_exclus ?? []}
            onChange={(next) => update({ secteurs_naf_exclus: next })}
          />
        </Card>

        <Card title="Company status" enforcement="pappers">
          <TriStateRow
            label="Trading status"
            value={draft.entreprise_active ?? null}
            options={[
              { value: null, label: "Any" },
              { value: true, label: "Active only" },
              { value: false, label: "Ceased only" },
            ]}
            onChange={(next) => update({ entreprise_active: next })}
          />
          <p className="text-xs text-slate-500">
            Sent to Pappers as <code>entreprise_cessee</code>, inverted:
            &ldquo;Active only&rdquo; asks for companies that have not
            ceased trading. &ldquo;Any&rdquo; omits the filter.
          </p>
        </Card>

        <Card title="Other thresholds" enforcement="unapplied">
          <SliderRow
            label="Max BODACC publications"
            min={0}
            max={10}
            step={1}
            value={draft.publications_bodacc_max ?? 0}
            display={String(draft.publications_bodacc_max ?? 0)}
            onChange={(n) => update({ publications_bodacc_max: n })}
          />
          <SliderRow
            label="Max directors"
            min={1}
            max={200}
            step={1}
            value={draft.nb_dirigeants_max ?? 1}
            display={String(draft.nb_dirigeants_max ?? 1)}
            onChange={(n) => update({ nb_dirigeants_max: n })}
          />
          <SliderRow
            label="Headcount variation tolerance"
            min={0}
            max={1}
            step={0.05}
            value={draft.variation_effectif_tolerance ?? 0}
            display={`${Math.round((draft.variation_effectif_tolerance ?? 0) * 100)}%`}
            // Rounded: a 0.05 step accumulates to 0.15000000000000002,
            // which the server accepts and then stores forever.
            onChange={(n) =>
              update({ variation_effectif_tolerance: Math.round(n * 100) / 100 })
            }
          />
        </Card>
      </fieldset>

      {canEdit && (
        <div className="flex items-center justify-end gap-3">
          <Button
            type="button"
            variant="ghost"
            onClick={reset}
            disabled={!isDirty || submitting}
          >
            Reset
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={save}
            disabled={!isDirty || submitting}
          >
            {submitting && (
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            )}
            Save changes
          </Button>
        </div>
      )}
    </section>
  );
}

// ─── Enforcement marks ───────────────────────────────────────────────────

/** Where a criterion is actually enforced, which is also where it does or
 *  does not cost credits.
 *
 *  `pappers` — in `_SENT_CRITERIA` (or handled explicitly beside it):
 *  it becomes a `/recherche` query parameter, so excluded rows are never
 *  returned and never billed.
 *
 *  `unapplied` — in `POST_FILTER_CRITERIA`. The name is a leftover and
 *  the backend says so: the 2026-09-03 coherence audit (finding F-3)
 *  found no code path applying any of the five, here or in the legacy —
 *  "an inherited fiction". So this must NOT read "applied after
 *  receipt": an operator who believes that would trust a filter that
 *  drops nothing. What is true is the billing — /recherche bills 0.1
 *  credit per row returned, and these five stop no row from being
 *  returned. Rename the mark when the day-of-key probe moves one of them
 *  into `_SENT_CRITERIA`, or when someone writes the local filter. */
type Enforcement = "pappers" | "unapplied";

const ENFORCEMENT_TAGS: Record<
  Enforcement,
  { label: string; className: string; title: string }
> = {
  pappers: {
    label: "Filtered by Pappers",
    className: "border-emerald-200 bg-emerald-50 text-emerald-700",
    title:
      "Sent as a /recherche query parameter — excluded rows are never returned, so they cost nothing.",
  },
  unapplied: {
    label: "Not applied · still billed",
    className: "border-slate-200 bg-white text-slate-500",
    title:
      "Pappers has no parameter for this and nothing filters it locally, so rows it would exclude are still returned and still billed 0.1 credit.",
  },
};

function EnforcementTag({ kind }: { kind: Enforcement }) {
  const tag = ENFORCEMENT_TAGS[kind];
  return (
    <span
      title={tag.title}
      className={cn(
        "inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[10px] font-medium",
        tag.className,
      )}
    >
      {tag.label}
    </span>
  );
}

// ─── Layout primitives ───────────────────────────────────────────────────

function Card({
  title,
  enforcement,
  children,
}: {
  title: string;
  /** Set only when every criterion in the card shares one fate. Mixed
   *  cards (Financials, Legal forms) mark their rows instead. */
  enforcement?: Enforcement;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-plane bg-white p-6 shadow-plane">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h3 className="text-sm font-medium tracking-tight text-slate-900">
          {title}
        </h3>
        {enforcement && <EnforcementTag kind={enforcement} />}
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

interface SliderRowProps {
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  display: string;
  enforcement?: Enforcement;
  onChange: (next: number) => void;
}

function SliderRow({
  label,
  min,
  max,
  step,
  value,
  display,
  enforcement,
  onChange,
}: SliderRowProps) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-2 text-sm text-slate-700">
          {label}
          {enforcement && <EnforcementTag kind={enforcement} />}
        </span>
        <span className="font-mono text-xs text-slate-500">{display}</span>
      </div>
      <Slider
        value={[value]}
        min={min}
        max={max}
        step={step}
        onValueChange={(arr) => onChange(arr[0])}
      />
    </div>
  );
}

interface TriStateRowProps<T> {
  label: string;
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (next: T) => void;
}

/** A segmented control for a field whose "unset" is a real, meaningful
 *  third state rather than a missing value — `entreprise_active` filters
 *  on active OR on ceased, and null asks for neither. A checkbox would
 *  have to invent one of the three. */
function TriStateRow<T extends string | number | boolean | null>({
  label,
  value,
  options,
  onChange,
}: TriStateRowProps<T>) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-sm text-slate-700">{label}</span>
      <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-0.5">
        {options.map((option) => (
          <button
            key={String(option.value)}
            type="button"
            onClick={() => onChange(option.value)}
            className={cn(
              "rounded-md px-3 py-1.5 text-xs font-medium transition-all",
              value === option.value
                ? "bg-white text-slate-900 shadow-sm-tile"
                : "text-slate-500 hover:text-slate-700",
            )}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

interface DualRangeRowProps {
  label: string;
  min: number;
  max: number;
  step: number;
  value: [number, number];
  displayLeft: string;
  displayRight: string;
  enforcement?: Enforcement;
  onChange: (next: [number, number]) => void;
}

function DualRangeRow({
  label,
  min,
  max,
  step,
  value,
  displayLeft,
  displayRight,
  enforcement,
  onChange,
}: DualRangeRowProps) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-2 text-sm text-slate-700">
          {label}
          {enforcement && <EnforcementTag kind={enforcement} />}
        </span>
        <span className="font-mono text-xs text-slate-500">
          {displayLeft} · {displayRight}
        </span>
      </div>
      <Slider
        value={value}
        min={min}
        max={max}
        step={step}
        minStepsBetweenThumbs={1}
        onValueChange={(arr) => {
          // Defensive: Radix can fire single-element arrays for a beat
          // while the second thumb mounts.
          if (arr.length === 2) onChange([arr[0], arr[1]]);
        }}
      />
    </div>
  );
}

// ─── Department picker ──────────────────────────────────────────────────

interface DepartmentsPickerProps {
  value: string[];
  onChange: (next: string[]) => void;
}

function DepartmentsPicker({ value, onChange }: DepartmentsPickerProps) {
  const { departments } = useDepartments("FR");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  const byCode = useMemo(() => {
    const m = new Map<string, Department>();
    for (const d of departments) m.set(d.code, d);
    return m;
  }, [departments]);

  const byRegion = useMemo(() => {
    const groups = new Map<string, Department[]>();
    for (const d of departments) {
      const arr = groups.get(d.region) ?? [];
      arr.push(d);
      groups.set(d.region, arr);
    }
    return Array.from(groups.entries()).sort((a, b) =>
      a[0].localeCompare(b[0]),
    );
  }, [departments]);

  const q = search.trim().toLowerCase();
  function matches(d: Department): boolean {
    if (!q) return true;
    return (
      d.code.includes(q) ||
      d.name.toLowerCase().includes(q) ||
      d.region.toLowerCase().includes(q)
    );
  }

  function add(code: string) {
    if (value.includes(code)) return;
    onChange([...value, code]);
  }
  function remove(code: string) {
    onChange(value.filter((c) => c !== code));
  }

  return (
    <div className="space-y-2">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((code) => {
            const d = byCode.get(code);
            return (
              <span
                key={code}
                className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-xs text-rose-700"
              >
                <span className="font-mono">{code}</span>
                {d && <span>· {d.name}</span>}
                <button
                  type="button"
                  onClick={() => remove(code)}
                  className="text-rose-400 hover:text-rose-700"
                  aria-label={`Remove ${code}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}
      <Popover
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) setSearch("");
        }}
      >
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="text-xs">
            <Plus className="mr-1 h-3.5 w-3.5" /> Add department
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[350px] p-2" align="start">
          <Input
            placeholder="Search by name, code or region…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="mb-2 h-8 text-sm"
          />
          <div className="max-h-[320px] overflow-y-auto">
            {byRegion.map(([region, depts]) => {
              const visible = depts
                .filter((d) => !value.includes(d.code))
                .filter(matches);
              if (visible.length === 0) return null;
              return (
                <div key={region}>
                  <div className="px-2 pb-1 pt-2 text-[10px] uppercase tracking-wider text-slate-400">
                    {region}
                  </div>
                  {visible.map((d) => (
                    <button
                      key={d.code}
                      type="button"
                      onClick={() => add(d.code)}
                      className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm hover:bg-slate-100"
                    >
                      <span className="font-mono text-slate-400">
                        {d.code}
                      </span>
                      <span className="truncate">{d.name}</span>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

// ─── NAF picker ──────────────────────────────────────────────────────────

interface NafSectionsPickerProps {
  value: string[];
  onChange: (next: string[]) => void;
}

function NafSectionsPicker({ value, onChange }: NafSectionsPickerProps) {
  const { sections } = useNafSections("FR");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  const byCode = useMemo(() => {
    const m = new Map<string, NafSection>();
    for (const s of sections) m.set(s.code, s);
    return m;
  }, [sections]);

  const q = search.trim().toLowerCase();
  function matches(s: NafSection): boolean {
    if (!q) return true;
    return s.code.includes(q) || s.label.toLowerCase().includes(q);
  }

  function add(code: string) {
    if (value.includes(code)) return;
    onChange([...value, code]);
  }
  function remove(code: string) {
    onChange(value.filter((c) => c !== code));
  }

  return (
    <div className="space-y-2">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((code) => {
            const s = byCode.get(code);
            return (
              <span
                key={code}
                className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-xs text-rose-700"
              >
                <span className="font-mono">{code}</span>
                {s && <span>· {s.label}</span>}
                <button
                  type="button"
                  onClick={() => remove(code)}
                  className="text-rose-400 hover:text-rose-700"
                  aria-label={`Remove ${code}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}
      <Popover
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) setSearch("");
        }}
      >
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="text-xs">
            <Plus className="mr-1 h-3.5 w-3.5" /> Add NAF sector
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[350px] p-2" align="start">
          <Input
            placeholder="Search by code or label…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="mb-2 h-8 text-sm"
          />
          <div className="max-h-[320px] overflow-y-auto">
            {sections
              .filter((s) => !value.includes(s.code))
              .filter(matches)
              .map((s) => (
                <button
                  key={s.code}
                  type="button"
                  onClick={() => add(s.code)}
                  className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm hover:bg-slate-100"
                >
                  <span className="font-mono text-slate-400">{s.code}</span>
                  <span className="truncate">{s.label}</span>
                </button>
              ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────

/** The server validates this block now, so its refusals carry the
 *  reason and the field — "ca_min (5) must not exceed ca_max (3)",
 *  "Input should be greater than or equal to 1". Swallowing that into a
 *  generic failure would put the operator back where the whole chantier
 *  started: a rejected save with nothing to act on. */
function extractMessage(err: unknown): string {
  if (!axios.isAxiosError(err)) return "Couldn't save search criteria.";
  const detail = (err.response?.data as { detail?: unknown } | undefined)
    ?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0] as { msg?: string; loc?: unknown[] };
    if (typeof first?.msg === "string") {
      // Pydantic prefixes model-level failures with "Value error, ".
      const msg = first.msg.replace(/^Value error,\s*/, "");
      // `loc` reads ["body", "search_criteria", "nb_dirigeants_max"];
      // the last segment is the field the operator has to fix.
      const field = Array.isArray(first.loc)
        ? first.loc.filter((part) => typeof part === "string").at(-1)
        : undefined;
      return field && field !== "search_criteria" ? `${field}: ${msg}` : msg;
    }
  }
  return "Couldn't save search criteria.";
}
